import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ShoppingBag, ArrowRight, Home, Package, Loader2 } from 'lucide-react';
import { useRestaurantCart, checkoutClient } from '@happyspace/sdk/restaurant';

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const { clearCart } = useRestaurantCart();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    clearCart();
    if (orderId) {
      setLoading(true);
      checkoutClient
        .getOrder(orderId)
        .then((result) => setOrder(result))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [orderId]);

  return (
    <div className="pt-20 min-h-screen bg-[#F9F6F0] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 bg-[#0054e9] rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-12 h-12 text-white" />
          </motion.div>

          <h1 className="text-3xl font-bold text-gray-900 mb-3">Order Confirmed!</h1>
          <p className="text-gray-500 mb-6 leading-relaxed">
            Thank you for choosing Dil Se Desi! Your order has been received and our
            kitchen is already working on it with love.
          </p>

          {orderId && (
            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Order Number</p>
              <p className="font-mono font-bold text-gray-900 text-lg">
                #{orderId.slice(-8).toUpperCase()}
              </p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading order details...</span>
            </div>
          )}

          {order && (
            <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left">
              <h3 className="font-bold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span>Order Summary</span>
              </h3>
              {order.items?.map((item: any, i: number) => (
                <div key={i} className="flex justify-between text-sm text-gray-600 py-1">
                  <span>{item.quantity}x {item.name}</span>
                  <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              {order.total && (
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-bold text-gray-900">
                  <span>Total</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 rounded-2xl p-4 mb-8 text-sm text-[#0054e9]">
            You will receive updates as your order progresses.
          </div>

          <div className="flex flex-col gap-3">
            {orderId && (
              <Link
                to={`/order-status/${orderId}`}
                className="flex items-center justify-center gap-2 bg-[#0054e9] text-white px-8 py-4 rounded-full font-bold hover:bg-[#015796] transition-colors"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Track Order</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link
              to="/menu"
              className="flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-full font-bold hover:border-[#0054e9] hover:text-[#0054e9] transition-colors"
            >
              Continue Shopping
            </Link>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-gray-400 hover:text-gray-700 font-semibold transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
